// NATIVE 
import React, { useEffect, useState } from 'react';

// INSTALLED 
import Box from '@mui/material/Box';
import { Formik, Form } from 'formik';
import Step from '@mui/material/Step';
import { useDispatch, useSelector } from 'react-redux';
import Stepper from '@mui/material/Stepper';
import StepLabel from '@mui/material/StepLabel';
import { Alert, Button, Card, Divider } from '@mui/material';



// CUSTOM 
import { storeEditCompany } from '../../store/company';
import { SnackAlert } from '../../components/SnackAlert';
import MultipleSnackbars from '../../components/MultipleSnackbars';
import { mtDividerUpTable, timeToClosedToast } from '../../commons';
import { closeSessionGlobal, hasPemissions } from '../../commons/utils';
import { ModalSessionExpired } from '../../components/generics/ModalSessionExpired';
import { BreadCrumbsAndTitle } from '../../components/generics/BreadCrumbsAndTitle';
import validationSchema from '../../components/empresa/editar/formModels/validationSchema';
import formInitialValues from '../../components/empresa/editar/formModels/formInitialValues';
import editCompanyFormModel from '../../components/empresa/editar/formModels/editCompanyFormModel';
import { DatosBasicosEditar, SeguridadSocialEditar, ArchivosEditar, LogosNombreComercialEditar, LogosRazonSocialEditar, FormaDePagoEditar } from '../../components/empresa/editar';
import { useNavigate } from 'react-router-dom';







/**
 * EditarCompania component handles the editing process of a company.
 * It includes a multi-step form with validation and submission logic.
 * 
 * @component
 * 
 * @returns {JSX.Element} The rendered component.
 * 
 * @example
 * <EditarCompania />
 * 
 * @remarks
 * This component uses Formik for form handling and validation, and Redux for state management.
 * It includes multiple steps for different sections of the form, such as basic information, payment methods, social security, files, and logos.
 * 
 * @function
 * @name EditarCompania
 * 
 * @description
 * The component manages the following states:
 * - `data`: An array to store error messages.
 * - `activeStep`: The current step in the multi-step form.
 * - `percentaje_c_Company`: The percentage completion of the company form.
 * - `timeAlert`: The duration for which the alert should be displayed.
 * - `messagesAlerts`: An array to store alert messages.
 * - `isSubmiting`: A boolean indicating if the form is being submitted.
 * - `modalSessionExpired`: A boolean indicating if the session has expired.
 * - `alert_snack`: A boolean indicating if the snackbar alert should be displayed.
 * - `msgSnack`: An object containing the message and status for the snackbar alert.
 * 
 * The component includes the following main functions:
 * - `_renderStepContent`: Renders the content for each step based on the current step index.
 * - `_submitForm`: Handles the form submission logic, including dispatching the action to store the edited company and validating the response.
 * - `lastStep`: Moves to the previous step in the form.
 * - `_handleSubmit`: Handles the form submission, including permission checks and moving to the next step or submitting the form.
 * - `validateLsPermissions`: Validates if the user has the necessary permissions to edit the company.
 * - `validateResponse`: Validates the response from the server and handles different response codes.
 * 
 * The component also includes the following UI elements:
 * - `ModalSessionExpired`: A modal that is displayed when the session has expired.
 * - `MultipleSnackbars`: A component to display multiple snackbar alerts.
 * - `SnackAlert`: A component to display a single snackbar alert.
 * - `BreadCrumbsAndTitle`: A component to display breadcrumbs and the title of the page.
 * - `Stepper`: A component to display the steps of the form.
 * - `Formik`: A component to handle the form state and validation.
 * - `Card`: A component to display the form content.
 * - `Button`: A component to display the navigation buttons for the form.
 */
export const EditarCompania = () => {

  // USE DISPATCH 
  const dispatch = useDispatch();


  // NAVIGATE 
  const navigate = useNavigate();


  // REDUX
  const { company }:any  = useSelector( (state:any) => state.company );

  
  const { formId, formField } = editCompanyFormModel;

  const [data, setMsgErrors] = useState([]);
  
  const [activeStep, setActiveStep] = useState(0);
  const [percentaje_c_Company, setPercentajeCCompany] = useState(0);
  const currentValidationSchema = validationSchema[activeStep];

  const [timeAlert, setTimeAlert] = React.useState(timeToClosedToast);
  const [messagesAlerts, setMessagesAlerts] = React.useState<any>([]);


  const [isSubmiting, setIsSubmiting] = React.useState<boolean>(false);

  const [modalSessionExpired, setModalSessionExpired] = useState(false);

  

  const [alert_snack, setAlertSnack] = useState(false);
  const [msgSnack, setMsgSnack] = useState({
    msg: '',
    status: 'success'
  });


  useEffect(() => {
          
    if ( !company || Object.keys(company).length == 0 ) {
          navigate("/configuracion/compania");
    }

  }, [company]);
  
  

  const steps: string[] = ['Informacion Basica', 'Forma de Pago', 'Seguridad Social', 'Archivos', 
                     'Logos Nombre Comercial', 'Logos Razón Social'];

  const isLastStep = activeStep === steps.length - 1;

  const isStepFailed = (step: number) => {
    return step === 0;
  };



  const  _renderStepContent = (steps: any) => {
    switch (steps) {
      case 0:
        return <DatosBasicosEditar formField={formField} />; 
      case 1:
        return <FormaDePagoEditar formField={formField} />;
      case 2:
        return <SeguridadSocialEditar formField={formField} />;
      case 3:
        return <ArchivosEditar formField={formField} />;
      case 4:
        return <LogosNombreComercialEditar formField={formField} />;
      case 5:
        return <LogosRazonSocialEditar formField={formField} />;
      default:
        return <div>Not Found</div>; 
    }
  }



  const _submitForm = async(values:any, actions:any) => {
    
    actions.setSubmitting(true);


    setIsSubmiting(true);

    const response = await dispatch( storeEditCompany(values, setPercentajeCCompany) );
    
    const result = await validateResponse(response, ' ( Editar Compañia ) ');

    setIsSubmiting(false);

    
    if (!result) {
      return false;
    }

    if (response.ok) {
      
      const errors:any = {
        msg: response.message,
        status: 'success'
      };
    
      await setMsgSnack(errors);
      await setAlertSnack(true);

      // setActiveStep(0);
      // actions.resetForm();
      actions.setSubmitting(false);
      
      setTimeout(() => {
        navigate("/configuracion/compania");
      }, 1200);

    }
    
  }

  const lastStep = () => {

      if (activeStep == 0) return;
      
      setActiveStep(activeStep - 1);
  }
  

  const _handleSubmit = async(values:any, actions:any) => {


    const havePermissions = await hasPemissions('modulo.configuracion', 'modulo.compania', 'edit' );
      
    if( !havePermissions ){

      validateLsPermissions('No tienes pemisos para (editar) compañia.');

      return false;                    
        
    }

   
    if (isLastStep) {
      _submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  }


  const validateLsPermissions = async(message:string) => {
    
    const errors:any = {
      msg: message,
      status: 'error'
    };
  
    await setMsgSnack(errors);
    await setAlertSnack(true);
  
  
  }



  const validateResponse = async(response:any, textActionConcat: string) => {
    
    
        if (  response.code == 401  ) {
          await localStorage.removeItem('lastSelectedQypht');
          await closeSessionGlobal();
          setModalSessionExpired(true);
          return false;
        }

      
        // 403 NO TIENE PERMISSO PARA REALIZAR ESA ACCION 
        if (!response.ok && response.code == 403 ) { 

          const errors:any = {
              msg: response.errors[0]+' '+textActionConcat,
              status: 'error'
          };
          
          await setMsgSnack(errors);
          await setAlertSnack(true);

          return false;
        }

        
        // 400 ERROR EN DATOS ENVIADOS MAYORMENTE PROPIEDAD INVALIDA O MAL FORMATEADO 
        if (!response.ok && response.code == 400 ) { 

          const errors:any = [];

          response.errors.forEach( (element:any) => {
              
              errors.push({
                type: 'error',
                text: element
              });
          });

            
          setMessagesAlerts(errors);
          return false;

        }

        return true;
      
  }




  return (
    <Box sx={{ width: '100%' }}>
     


      <ModalSessionExpired  open={modalSessionExpired}  />
                
      <MultipleSnackbars messagesList={messagesAlerts} position={"bottom-left"} autoClose={timeAlert} />
      <SnackAlert open={alert_snack} onclick={setAlertSnack} text={msgSnack.msg} severity={msgSnack.status}/>
      
      
      <BreadCrumbsAndTitle 
                            title="Editar Compañia" 
                            mainModule="configuracion" 
                            parent="Editar Compañia"
                            action="/configuracion/compania" />

      <Divider sx={{ marginBottom: mtDividerUpTable }}/>
           
      
      
      <Stepper activeStep={activeStep}>
        {steps.map((label:any, index:any) => {
          const labelProps: {
            optional?: React.ReactNode;
            error?: boolean;
          } = {};

          return (
            <Step key={label}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

         <Formik
            initialValues={formInitialValues}
            validationSchema={currentValidationSchema}
            onSubmit={_handleSubmit}
          >
         <Card style={{ marginTop: 50,  padding: 20}}>
           <Form id={formId}>
             {_renderStepContent(activeStep)}
            <div style={{ display: 'flex', justifyContent: 'end', marginTop: 25}}>
              

              {
                activeStep > 0 && <Button
                                    style={{margin:5}}
                                    variant="contained"
                                    onClick={lastStep}
                                    color="primary">
                                    Atras
                                  </Button>
              }
              <Button
                  disabled={isSubmiting}
                  style={{margin:5}}
                  type="submit"
                  variant="contained"
                  color={ isLastStep ? "success" : "primary"}
                >
                  {isLastStep ? "Editar Compañia" : "Siguiente"}
              </Button>

              
            </div>
            </Form>
         </Card>
         
         </Formik>
    </Box>
  )
}
